import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * Iter59.p24 — Read the current theme's chart palette from CSS custom
 * properties on <html>. Rerenders when the theme changes (data-theme
 * attribute mutation).
 *
 * Usage:
 *   const c = useChartColors();
 *   <Area stroke={c.series[0]} fill={c.series[0]} />
 *   <CartesianGrid stroke={c.grid} />
 *   <Tooltip contentStyle={{ background: c.tooltipBg, color: c.tooltipText }} />
 */
function _readPalette() {
    if (typeof window === "undefined") {
        return {
            grid: "#E5E9F0",
            axis: "#94A3B8",
            label: "#475569",
            tooltipBg: "#FFFFFF",
            tooltipText: "#0F172A",
            background: "#FFFFFF",
            series: ["#1B2A56", "#0E9F6E", "#F59E0B", "#A8324A", "#2563EB", "#8B5CF6"],
            statusSuccess: "#0E9F6E",
            statusWarning: "#F59E0B",
            statusError: "#DC2626",
        };
    }
    const s = getComputedStyle(document.documentElement);
    const v = (name, fallback) => (s.getPropertyValue(name).trim() || fallback);
    return {
        grid:         v("--chart-grid",         "#E5E9F0"),
        axis:         v("--chart-axis",         "#94A3B8"),
        label:        v("--chart-label",        "#475569"),
        tooltipBg:    v("--chart-tooltip-bg",   "#FFFFFF"),
        tooltipText:  v("--chart-tooltip-text", "#0F172A"),
        background:   v("--chart-bg",           "#FFFFFF"),
        series: [1, 2, 3, 4, 5, 6].map((n) =>
            v(`--chart-series-${n}`, "#1B2A56"),
        ),
        statusSuccess: v("--status-success", "#0E9F6E"),
        statusWarning: v("--status-warning", "#F59E0B"),
        statusError:   v("--status-error",   "#DC2626"),
    };
}

export default function useChartColors() {
    const [palette, setPalette] = useState(_readPalette);

    const refresh = useCallback(() => setPalette(_readPalette()), []);

    useEffect(() => {
        if (typeof window === "undefined") return undefined;
        // React to data-theme changes on <html>.
        const target = document.documentElement;
        const obs = new MutationObserver(() => refresh());
        obs.observe(target, {
            attributes: true,
            attributeFilter: ["data-theme", "style"],
        });
        // Also react to explicit "asl:theme" events dispatched by the
        // ThemeProvider on user-driven changes.
        window.addEventListener("asl:theme", refresh);
        return () => {
            obs.disconnect();
            window.removeEventListener("asl:theme", refresh);
        };
    }, [refresh]);

    return useMemo(() => palette, [palette]);
}
